import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { MARCHI, applicaMarchio, type Marchio, type MarchioId } from './marchi';
import { risolviMarchio } from './marca';
import { usePreferenze } from './preferenze';

interface ContestoMarchio {
  marchio: Marchio;
  setMarchio(id: MarchioId): void;
  /** true only on a demo host: the brand switcher may be shown. On an installer
   *  host the brand is fixed by DNS and must not be switchable (ADR-059 §4). */
  puoCambiare: boolean;
}

const Contesto = createContext<ContestoMarchio | null>(null);

export function BrandProvider({ children }: { children: ReactNode }) {
  const salvato = usePreferenze((s) => s.marchio);
  const setMarchioPref = usePreferenze((s) => s.setMarchio);

  // Brand comes from the HOST (unforgeable); `?brand=` is honoured only on a
  // demo host. Resolved once from location — a per-installer host can never be
  // made to render another installer's brand from a URL string (ADR-059 §4).
  const risoluzione = useMemo(() => {
    if (typeof window === 'undefined') {
      return { id: salvato ?? 'cstai', bloccatoDaHost: false, demo: false } as const;
    }
    return risolviMarchio(window.location.hostname, window.location.search, salvato);
  }, [salvato]);

  const id = risoluzione.id;

  useEffect(() => {
    // Only remember a demo override; never persist a host-fixed brand as if the
    // user had chosen it.
    if (risoluzione.demo && id !== salvato) setMarchioPref(id);
  }, [risoluzione.demo, id, salvato, setMarchioPref]);

  const marchio = MARCHI[id];
  useEffect(() => applicaMarchio(marchio), [marchio]);

  const valore = useMemo<ContestoMarchio>(
    () => ({
      marchio,
      // A host-locked brand cannot be switched; a demo may be.
      setMarchio: risoluzione.bloccatoDaHost ? () => {} : setMarchioPref,
      puoCambiare: risoluzione.demo,
    }),
    [marchio, risoluzione.bloccatoDaHost, risoluzione.demo, setMarchioPref],
  );
  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

export function useMarchio(): ContestoMarchio {
  const c = useContext(Contesto);
  if (!c) throw new Error('useMarchio fuori da BrandProvider');
  return c;
}
